import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDismiss } from '../../hooks/useDismiss';
import type { MermaidStyle, VisualizationConfig } from '@deckspace/types';
import { getMermaidThemeVariables, getMermaidDesignPreviewVariables } from '../../theme';
import { useTheme } from '../../hooks/useTheme';
import { normalizeColor, parseColorToRgb } from '../../theme/color-normalization';
import { Minus, Plus } from 'lucide-react';

/** Data shape consumed by the internal renderer. */
export interface MermaidRendererData {
  code?: string;
  mermaidStyle?: MermaidStyle;
}

// Lazy load mermaid (~2MB) and react-zoom-pan-pinch only when needed
const mermaidPromise = import('mermaid').then((m) => m.default);
const zoomPanPinchPromise = import('react-zoom-pan-pinch');

// Render mutex to prevent race conditions when multiple MermaidCharts render simultaneously.
// Mermaid.js is a singleton - initialize() sets global config that affects all subsequent render() calls.
// Without synchronization, concurrent renders can use the wrong config (last initialize() wins).
let renderLock: Promise<void> = Promise.resolve();
function withRenderLock<T>(fn: () => Promise<T>): Promise<T> {
  const previousLock = renderLock;
  let releaseLock: () => void;
  renderLock = new Promise((resolve) => {
    releaseLock = resolve;
  });
  return previousLock.then(fn).finally(() => releaseLock());
}

interface RendererProps {
  data: MermaidRendererData;
  config: VisualizationConfig;
  visualizationId?: string;
  interactionMode?: 'click-to-interact' | 'always-interactive';
}

// Store zoom state per visualization ID (persists across tab switches)
const zoomStateStore: Record<string, { scale: number; positionX: number; positionY: number }> = {};

// Cache rendered SVG content to avoid re-rendering when switching tabs
const svgCache: Record<string, { svg: string; code: string; dark: boolean }> = {};

/**
 * Calculate relative luminance of a color (0-1 scale)
 * Based on WCAG 2.0 formula
 */
function getLuminance(r: number, g: number, b: number): number {
  const mapped = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  const rs = mapped[0] ?? 0;
  const gs = mapped[1] ?? 0;
  const bs = mapped[2] ?? 0;
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

// parseColor is provided by parseColorToRgb from ../theme/color-normalization
// which also handles display-p3 format from wide-gamut displays

/**
 * Determine if text should be light or dark based on background color
 * Returns true if background is dark (needs light text)
 */
function needsLightText(bgColor: string): boolean {
  const rgb = parseColorToRgb(bgColor);
  if (!rgb) return false; // Default to dark text if we can't parse

  const luminance = getLuminance(rgb.r, rgb.g, rgb.b);
  // Threshold of 0.5 - below this, background is "dark" and needs light text
  return luminance < 0.5;
}

/**
 * Get the background color of a node by finding its rect/polygon fill
 */
function getNodeBackgroundColor(node: Element): string | null {
  const shape = node.querySelector('rect, polygon, circle, ellipse, path');
  if (!shape) return null;

  // Check inline style first (highest priority)
  const style = shape.getAttribute('style');
  if (style) {
    const fillMatch = style.match(/fill:\s*([^;!]+)/);
    if (fillMatch?.[1]) return fillMatch[1].trim();
  }

  // Then check fill attribute
  const fill = shape.getAttribute('fill');
  if (fill && fill !== 'none') return fill;

  // Finally, check computed style (for CSS-applied fills)
  // This catches cases where Mermaid theme applies fill via CSS
  // normalizeColor handles display-p3 format from wide-gamut displays
  if (shape instanceof SVGElement || shape instanceof HTMLElement) {
    const computed = getComputedStyle(shape);
    const computedFill = computed.fill;
    if (computedFill && computedFill !== 'none') {
      return normalizeColor(computedFill);
    }
  }

  return null;
}

/**
 * Pre-process Mermaid code to convert \n inside quoted labels to <br/> for proper line breaks.
 * Mermaid doesn't interpret \n as line breaks inside node labels - it needs <br/> tags.
 */
function preprocessMermaidCode(code: string): string {
  // Match quoted strings in node definitions: ["..."] or ("...") or {"..."} etc.
  // Replace \n inside these quoted strings with <br/>
  return code.replace(/\["([^"]+)"\]/g, (match, content) => {
    // Replace literal \n with <br/> for line breaks
    const processed = content.replace(/\\n/g, '<br/>');
    return `["${processed}"]`;
  });
}

/**
 * Post-process SVG to fix text legibility issues.
 *
 * When users specify inline `style` directives in Mermaid (e.g., `style NodeA fill:#e1f5fe`),
 * Mermaid doesn't automatically pick a contrasting text color. This function fixes that by:
 * 1. Detecting node background color and choosing contrasting text
 * 2. Making edge labels readable with proper backgrounds
 * 3. Making subgraph labels visible against the page background
 */
function postProcessSvg(container: HTMLElement, isDark: boolean): void {
  const svg = container.querySelector('svg');
  if (!svg) return;

  // Read colors from CSS variables to stay in sync with theme
  // normalizeColor converts display-p3 format to sRGB hex on wide-gamut displays
  const getCssVar = (name: string, fallback: string): string => {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return normalizeColor(value) || fallback;
  };

  // Get theme-appropriate text and background colors from CSS variables
  const darkText = getCssVar('--color-text-primary', '#18181b');
  const lightText = isDark ? '#fafafa' : getCssVar('--color-text-inverse', '#fafafa');
  const darkBg = getCssVar('--color-surface', isDark ? '#2a2a28' : '#f4f4f5');
  const lightBg = getCssVar('--color-background', isDark ? '#09090b' : '#ffffff');

  // 1. Fix text inside nodes WITH CUSTOM STYLES only
  // Nodes using theme defaults already have appropriate text colors from mermaid
  const nodes = svg.querySelectorAll('.node');
  nodes.forEach((node) => {
    // Check if this node has a custom inline style (user-specified fill)
    const shape = node.querySelector('rect, polygon, circle, ellipse, path');
    const hasInlineStyle = shape?.getAttribute('style')?.includes('fill');

    // Only process nodes with custom styles - theme nodes are already correct
    if (!hasInlineStyle) return;

    const bgColor = getNodeBackgroundColor(node);
    // If we can detect background, choose contrasting text
    // If we can't detect, fall back to theme-appropriate text (light text in dark mode)
    const useLightText = bgColor ? needsLightText(bgColor) : isDark;
    const textColor = useLightText ? lightText : darkText;

    // Apply to all text elements in this node
    const textElements = node.querySelectorAll('text, tspan, .nodeLabel, span, p, div');
    textElements.forEach((el) => {
      if (el instanceof HTMLElement) {
        el.style.setProperty('color', textColor, 'important');
        el.style.setProperty('font-weight', '500', 'important');
      }
      if (el instanceof SVGElement) {
        el.setAttribute('fill', textColor);
        el.setAttribute('font-weight', '500');
      }
    });
  });

  // Also target foreignObject content inside nodes WITH CUSTOM STYLES (HTML inside SVG)
  const foreignContent = svg.querySelectorAll(
    'foreignObject span, foreignObject p, foreignObject div'
  );
  foreignContent.forEach((el) => {
    if (el instanceof HTMLElement) {
      const nodeParent = el.closest('.node');
      const isInEdgeLabel = el.closest('.edgeLabel') !== null;
      const isInCluster = el.closest('.cluster') !== null && !nodeParent;

      if (nodeParent) {
        // Only process nodes with custom inline styles
        const shape = nodeParent.querySelector('rect, polygon, circle, ellipse, path');
        const hasInlineStyle = shape?.getAttribute('style')?.includes('fill');
        if (!hasInlineStyle) return;

        // Node text - determine color based on background
        const bgColor = getNodeBackgroundColor(nodeParent);
        // If we can detect background, choose contrasting text
        // If we can't detect, fall back to theme-appropriate text (light text in dark mode)
        const useLightText = bgColor ? needsLightText(bgColor) : isDark;
        el.style.setProperty('color', useLightText ? lightText : darkText, 'important');
        el.style.setProperty('font-weight', '500', 'important');
      } else if (isInEdgeLabel) {
        // Edge labels - theme-aware
        el.style.setProperty('color', isDark ? lightText : darkText, 'important');
        el.style.setProperty('font-weight', '500', 'important');
      } else if (isInCluster) {
        // Cluster/subgraph labels - theme-aware (on page background)
        el.style.setProperty('color', isDark ? lightText : darkText, 'important');
        el.style.setProperty('font-weight', '600', 'important');
      }
    }
  });

  // 2. Fix edge label backgrounds and text
  const edgeLabelRects = svg.querySelectorAll('.edgeLabel rect, .labelBkg');
  edgeLabelRects.forEach((el) => {
    if (el instanceof SVGElement) {
      el.setAttribute('fill', isDark ? darkBg : lightBg);
      el.setAttribute('fill-opacity', '0.95');
    }
  });

  const edgeLabelText = svg.querySelectorAll(
    '.edgeLabel span, .edgeLabel text, .edgeLabel p, .edgeLabel tspan'
  );
  edgeLabelText.forEach((el) => {
    if (el instanceof HTMLElement) {
      el.style.setProperty('color', isDark ? lightText : darkText, 'important');
    }
    if (el instanceof SVGElement) {
      el.setAttribute('fill', isDark ? lightText : darkText);
    }
  });

  // 3. Fix subgraph/cluster labels - these sit on page background, need theme-aware colors
  // Mermaid uses various structures for cluster labels
  const clusterLabels = svg.querySelectorAll(
    '.cluster-label text, .cluster-label span, .cluster-label p, ' +
      '.cluster text, .cluster tspan, .cluster span, .cluster p, ' +
      'g.cluster > text, g.cluster > foreignObject span, g.cluster > foreignObject p'
  );
  clusterLabels.forEach((el) => {
    // Skip if this element is inside a node (nodes have their own styling)
    if (el.closest('.node')) return;

    if (el instanceof HTMLElement) {
      el.style.setProperty('color', isDark ? lightText : darkText, 'important');
    }
    if (el instanceof SVGElement) {
      el.setAttribute('fill', isDark ? lightText : darkText);
    }
  });

  // 4. Also fix any direct text children of clusters (subgraph titles)
  const clusters = svg.querySelectorAll('.cluster, g.cluster, [class*="subGraph"]');
  clusters.forEach((cluster) => {
    // Find the label - it's usually a direct child or in a specific structure
    const labelTexts = cluster.querySelectorAll(
      ':scope > text, :scope > g > text, :scope > foreignObject'
    );
    labelTexts.forEach((labelEl) => {
      // Don't process if it's inside a node
      if (labelEl.closest('.node')) return;

      if (labelEl instanceof SVGTextElement) {
        labelEl.setAttribute('fill', isDark ? lightText : darkText);
      }
      // For foreignObject, process its children
      if (labelEl.tagName === 'foreignObject') {
        const children = labelEl.querySelectorAll('span, p, div');
        children.forEach((child) => {
          if (child instanceof HTMLElement) {
            child.style.setProperty('color', isDark ? lightText : darkText, 'important');
          }
        });
      }
    });
  });

  // 5. Fix subgraph borders for dark mode visibility
  if (isDark) {
    const clusterRects = svg.querySelectorAll('.cluster rect, .cluster polygon, .cluster path');
    clusterRects.forEach((el) => {
      if (el instanceof SVGElement) {
        el.setAttribute('stroke', '#666');
      }
    });
  }

  // 6. Expand SVG viewBox to prevent clipping of labels at edges
  // Mermaid sometimes renders diagrams where subgraph labels extend beyond the viewBox
  const viewBox = svg.getAttribute('viewBox');
  if (viewBox) {
    const parts = viewBox.split(' ').map(Number);
    const minX = parts[0] ?? 0;
    const minY = parts[1] ?? 0;
    const width = parts[2] ?? 100;
    const height = parts[3] ?? 100;
    // Add significant padding to all sides to prevent edge clipping
    // Use larger padding on left side where subgraph labels often extend
    const paddingLeft = 200;
    const paddingOther = 50;
    svg.setAttribute(
      'viewBox',
      `${minX - paddingLeft} ${minY - paddingOther} ${width + paddingLeft + paddingOther} ${height + paddingOther * 2}`
    );
  }

  // 7. Set overflow visible on all foreignObjects for labels that extend beyond their containers
  const allForeignObjects = svg.querySelectorAll('foreignObject');
  allForeignObjects.forEach((fo) => {
    if (fo instanceof SVGElement) {
      fo.style.overflow = 'visible';
    }
  });

  // 8. Fix cluster label foreignObject widths - they're often too narrow for the text
  // We need to expand them so the text doesn't get clipped
  const clusterLabelFOs = svg.querySelectorAll('.cluster-label foreignObject');
  clusterLabelFOs.forEach((fo) => {
    if (fo instanceof SVGForeignObjectElement) {
      const textEl = fo.querySelector('span, p, div');
      if (textEl && textEl instanceof HTMLElement) {
        // Force text to not wrap and display inline
        textEl.style.whiteSpace = 'nowrap';
        textEl.style.display = 'inline-block';
        textEl.style.overflow = 'visible';

        // Get the current foreignObject position and dimensions
        const currentX = parseFloat(fo.getAttribute('x') || '0');
        const currentWidth = parseFloat(fo.getAttribute('width') || '0');

        // Calculate text width - use a large temporary width to measure
        fo.setAttribute('width', '1000');

        // Force reflow to measure
        const textWidth = textEl.scrollWidth || textEl.offsetWidth;

        // Set width to accommodate text with padding, and adjust x position
        const newWidth = Math.max(currentWidth, textWidth + 20);
        const widthDiff = newWidth - currentWidth;

        // Shift x left to keep label centered over subgraph, and expand width
        fo.setAttribute('x', String(currentX - widthDiff / 2));
        fo.setAttribute('width', String(newWidth));
      }
    }
  });
}

// Lazy-loaded zoom controls component
function ZoomControlsInner({
  useControls,
}: {
  useControls: () => {
    zoomIn: (step: number, duration: number) => void;
    zoomOut: (step: number, duration: number) => void;
    resetTransform: () => void;
  };
}) {
  const { zoomIn, zoomOut, resetTransform } = useControls();

  return (
    <div className="absolute top-2 right-2 flex items-center gap-1 bg-card/90 backdrop-blur-sm rounded-lg border border-border shadow-sm p-1 z-10">
      <button
        onClick={() => zoomOut(0.3, 200)}
        className="w-7 h-7 flex items-center justify-center rounded hover:bg-background text-muted-foreground hover:text-foreground transition-colors"
        title="Zoom out (or scroll down)"
      >
        <Minus size={16} />
      </button>
      <button
        onClick={() => resetTransform()}
        className="px-2 h-7 flex items-center justify-center rounded hover:bg-background text-xs text-muted-foreground hover:text-foreground transition-colors"
        title="Reset zoom (fit to view)"
      >
        Fit
      </button>
      <button
        onClick={() => zoomIn(0.3, 200)}
        className="w-7 h-7 flex items-center justify-center rounded hover:bg-background text-muted-foreground hover:text-foreground transition-colors"
        title="Zoom in (or scroll up)"
      >
        <Plus size={16} />
      </button>
    </div>
  );
}

export function MermaidRenderer({
  data,
  visualizationId,
  interactionMode = 'click-to-interact',
}: RendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const { colorScheme } = useTheme();
  const dark = colorScheme === 'dark';
  const [isInteractive, setIsInteractive] = useState(interactionMode === 'always-interactive');
  const [zoomPanPinch, setZoomPanPinch] = useState<typeof import('react-zoom-pan-pinch') | null>(
    null
  );
  const [isLibraryLoading, setIsLibraryLoading] = useState(true);

  // Design preview mode detection
  const isDesignPreview = !!data.mermaidStyle;

  // Generate a stable ID for charts that don't have a visualizationId
  // Include style hash for design preview mode to ensure different styles get different cache entries
  const styleHash = isDesignPreview
    ? `-design-${JSON.stringify(data.mermaidStyle).slice(0, 100)}`
    : '';
  const stableId = visualizationId ?? `mermaid-${data.code?.slice(0, 50)}${styleHash}`;

  // Check if we have a cached SVG for this visualization
  // Design preview mode ignores dark flag (user colors are fixed)
  const cached = svgCache[stableId];
  const cacheValid =
    cached && cached.code === data.code && (isDesignPreview || cached.dark === dark);

  // Initialize svgContent from cache if valid
  const [svgContent, setSvgContent] = useState<string | null>(cacheValid ? cached.svg : null);

  // Get saved zoom state for this visualization
  const savedState = zoomStateStore[stableId];

  // Track calculated initial transform for centering
  const [initialTransform, setInitialTransform] = useState<{
    scale: number;
    x: number;
    y: number;
    ready: boolean;
  }>({ scale: 1, x: 0, y: 0, ready: !!savedState });

  // Save zoom state when it changes
  const handleTransformChange = useCallback(
    (ref: { state: { scale: number; positionX: number; positionY: number } }) => {
      zoomStateStore[stableId] = {
        scale: ref.state.scale,
        positionX: ref.state.positionX,
        positionY: ref.state.positionY,
      };
    },
    [stableId]
  );

  // Reset interaction state when visualization changes (e.g. switching workspace tabs)
  useEffect(() => {
    if (interactionMode === 'click-to-interact') {
      setIsInteractive(false);
    }
  }, [stableId, interactionMode]);

  const stopInteraction = useCallback(() => setIsInteractive(false), []);
  useDismiss(wrapperRef, isInteractive && interactionMode === 'click-to-interact', stopInteraction);

  // Load zoom-pan-pinch library lazily
  useEffect(() => {
    zoomPanPinchPromise.then((module) => {
      setZoomPanPinch(module);
      setIsLibraryLoading(false);
    });
  }, []);

  // Clear cache when theme changes to force re-render with new colors
  // Skip in design preview mode - user colors are fixed
  useEffect(() => {
    if (isDesignPreview) return;

    // Clear cache for this visualization when dark mode changes
    if (svgCache[stableId]) {
      delete svgCache[stableId];
    }
    setSvgContent(null); // Force re-render
  }, [dark, isDesignPreview, stableId]);

  useEffect(() => {
    if (!data.code) {
      setError('No diagram code provided');
      return;
    }

    // Capture code after the check so TypeScript knows it's defined
    const code = data.code;

    // Check cache first - if valid, use cached SVG
    // Design preview mode ignores dark flag
    const cached = svgCache[stableId];
    if (cached && cached.code === code && (isDesignPreview || cached.dark === dark)) {
      setSvgContent(cached.svg);
      return;
    }

    const renderDiagram = async () => {
      try {
        setError(null);

        // Lazy load mermaid library (~2MB)
        const mermaid = await mermaidPromise;

        // Get theme variables - use design preview mode if mermaidStyle is specified
        const themeVariables =
          isDesignPreview && data.mermaidStyle
            ? getMermaidDesignPreviewVariables(data.mermaidStyle)
            : getMermaidThemeVariables();

        // Use render lock to prevent race conditions.
        // Mermaid is a singleton - initialize() sets global config for all subsequent renders.
        // Without this lock, concurrent components could interleave their initialize/render calls.
        const { svg } = await withRenderLock(async () => {
          mermaid.initialize({
            startOnLoad: false,
            theme: 'base',
            securityLevel: 'strict',
            themeVariables,
            flowchart: {
              padding: 20,
              nodeSpacing: 50,
              rankSpacing: 50,
              htmlLabels: true,
              useMaxWidth: false,
              subGraphTitleMargin: {
                top: 10,
                bottom: 10,
              },
            },
            sequence: {
              useMaxWidth: false, // Render at natural size, not scaled to container
              diagramMarginX: 50,
              diagramMarginY: 30,
              actorMargin: 80,
              width: 150, // Actor box width
              height: 65, // Actor box height
              boxMargin: 10,
              boxTextMargin: 5,
              noteMargin: 10,
              messageMargin: 35,
              mirrorActors: true,
            },
            state: {
              useMaxWidth: false,
            },
            er: {
              useMaxWidth: false,
            },
            journey: {
              useMaxWidth: false,
            },
            gantt: {
              useMaxWidth: false,
            },
            c4: {
              useMaxWidth: false,
            },
          });

          const id = `mermaid-${Date.now()}`;
          // Pre-process code to convert \n in quoted labels to <br/> for proper line breaks
          const processedCode = preprocessMermaidCode(code);

          return await mermaid.render(id, processedCode);
        });

        // Cache the rendered SVG
        // Design preview mode uses false for dark flag since it's irrelevant
        svgCache[stableId] = { svg, code, dark: isDesignPreview ? false : dark };
        setSvgContent(svg);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to render diagram');
        setSvgContent(null);
      }
    };

    renderDiagram();
  }, [data.code, data.mermaidStyle, dark, stableId, isDesignPreview]);

  // Post-process SVG after it's rendered to fix text legibility
  // Also calculate initial scale and center position to fit diagram in container
  useEffect(() => {
    // For measuring, we use measureRef which is always rendered
    // For post-processing, we use containerRef which is inside TransformWrapper
    const measureContainer = measureRef.current;
    const wrapper = wrapperRef.current;

    if (svgContent && measureContainer && wrapper) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        if (!measureRef.current || !wrapperRef.current) return;

        // Calculate scale to fit diagram in container using the measure container
        const svg = measureRef.current.querySelector('svg');
        if (svg) {
          const wrapperRect = wrapperRef.current.getBoundingClientRect();
          const svgWidth = svg.getBoundingClientRect().width;
          const svgHeight = svg.getBoundingClientRect().height;

          // Only scale down if diagram is larger than container
          // Leave some padding (0.95) for visual breathing room
          if (svgWidth > 0 && svgHeight > 0) {
            const scaleX = (wrapperRect.width * 0.95) / svgWidth;
            const scaleY = (wrapperRect.height * 0.95) / svgHeight;
            // Allow scaling up small diagrams (like sequence diagrams) to fill container
            // Cap at 2.5x to avoid excessive scaling that might look blurry
            const fitScale = Math.min(scaleX, scaleY, 2.5);

            // Only set initial transform if there's no saved state for this visualization
            if (!zoomStateStore[stableId]) {
              // Calculate position to center the scaled diagram
              const scaledWidth = svgWidth * fitScale;
              const scaledHeight = svgHeight * fitScale;
              const centerX = (wrapperRect.width - scaledWidth) / 2;
              const centerY = (wrapperRect.height - scaledHeight) / 2;

              setInitialTransform({
                scale: fitScale,
                x: centerX,
                y: centerY,
                ready: true,
              });
            } else {
              // If we have saved state, mark as ready but don't update transform
              setInitialTransform((prev) => ({ ...prev, ready: true }));
            }
          }
        }
      });
    }
  }, [svgContent, stableId]);

  // Post-process SVG in the actual container after TransformWrapper is ready
  // Skip in design preview mode - user has full control over colors
  useEffect(() => {
    if (svgContent && containerRef.current && initialTransform.ready && !isDesignPreview) {
      requestAnimationFrame(() => {
        if (containerRef.current) {
          postProcessSvg(containerRef.current, dark);
        }
      });
    }
  }, [svgContent, dark, initialTransform.ready, isDesignPreview]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[300px] text-destructive">
        <p className="font-medium">Failed to render diagram</p>
        <p className="text-sm text-muted-foreground mt-1">{error}</p>
        {data.code && (
          <pre className="mt-4 p-3 bg-card rounded text-xs text-muted-foreground max-w-full overflow-auto">
            {data.code}
          </pre>
        )}
      </div>
    );
  }

  if (!svgContent || isLibraryLoading || !zoomPanPinch) {
    return (
      <div className="flex items-center justify-center h-[300px] text-muted-foreground">
        Loading diagram...
      </div>
    );
  }

  const { TransformWrapper, TransformComponent, useControls } = zoomPanPinch;

  // Apply background color for design preview mode
  const wrapperStyle: React.CSSProperties =
    isDesignPreview && data.mermaidStyle?.background
      ? { backgroundColor: data.mermaidStyle.background }
      : {};

  return (
    <div ref={wrapperRef} className="relative h-full w-full" style={wrapperStyle}>
      {/* Hidden container for measuring SVG dimensions before showing */}
      <div
        ref={measureRef}
        className="absolute opacity-0 pointer-events-none"
        style={{ visibility: 'hidden' }}
        // SECURITY: svgContent is generated by mermaid.render(), not user-supplied HTML.
        // Mermaid parses diagram DSL and outputs sanitized SVG.
        // nosemgrep: react-dangerouslysetinnerhtml
        dangerouslySetInnerHTML={{ __html: svgContent }}
      />

      {/* Only render TransformWrapper once we have calculated the initial transform */}
      {initialTransform.ready && (
        <TransformWrapper
          key={stableId}
          initialScale={savedState?.scale ?? initialTransform.scale}
          initialPositionX={savedState?.positionX ?? initialTransform.x}
          initialPositionY={savedState?.positionY ?? initialTransform.y}
          minScale={0.1}
          maxScale={10}
          centerOnInit={false}
          wheel={{ step: 0.1, disabled: !isInteractive }}
          doubleClick={{ disabled: true }}
          panning={{ velocityDisabled: true, disabled: !isInteractive }}
          limitToBounds={false}
          onTransformed={handleTransformChange}
        >
          {isInteractive && <ZoomControlsInner useControls={useControls} />}
          <TransformComponent
            wrapperStyle={{
              width: '100%',
              height: '100%',
            }}
          >
            <div
              ref={containerRef}
              className="mermaid-content"
              // nosemgrep: react-dangerouslysetinnerhtml
              dangerouslySetInnerHTML={{ __html: svgContent }}
            />
          </TransformComponent>
        </TransformWrapper>
      )}

      {/* Click-to-interact overlay — visible on hover when not yet interactive */}
      {!isInteractive && interactionMode === 'click-to-interact' && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center cursor-pointer opacity-0 hover:opacity-100 transition-opacity duration-200"
          onClick={() => setIsInteractive(true)}
        >
          <div className="bg-black/30 dark:bg-black/40 absolute inset-0" />
          <span className="relative px-3 py-1.5 rounded-md bg-black/60 backdrop-blur-sm text-white text-sm font-medium">
            Click to interact
          </span>
        </div>
      )}

      {/* Show loading state while calculating initial transform */}
      {!initialTransform.ready && (
        <div className="flex items-center justify-center h-full text-muted-foreground">
          Loading diagram...
        </div>
      )}
    </div>
  );
}
